import { queueDuplicateTenantContent } from '@/actions/duplicate-tenant-content';
import { triggerCreateNewLanding } from '@/actions/landings-proxy/landings-proxy';
import { bootstrapNewLandingDefaults } from '@/actions/new-landing-bootstrap';
import { createSettings, createTenant, isTenantNameAvailable } from '@/actions/tenants';
import { LABELS_BY_LOCALES } from '@/constants/locales';
import { SupportedLocaleCode } from '@/services/locales/locales.models';
import { resolveTemplateSourceLocale } from '@/utils/duplicate-tenant';
import { ErrorWithContext, reportError } from '@/utils/error-reporting';
import { getLandingName } from '@/utils/landing';
import { Button, LoadingOverlay, toast, useModal } from '@payloadcms/ui';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

import { CREATE_NEW_LANDING_MODAL_SLUG } from '../CreateNewLandingModal';
import { CREATION_MODE } from './LandingSourceStep';
import { WizardStepHeader } from './WizardStepHeader';

import type { TenantListItem } from '@/actions/tenants';
import type { CreationMode } from './LandingSourceStep';

interface ConfirmLandingChoicesProps {
  name: string;
  supportedLocales: SupportedLocaleCode[];
  creationMode: CreationMode;
  templateTenant: TenantListItem | null;
  onBack: () => void;
}

export const ConfirmLandingChoices = ({ name, supportedLocales, creationMode, templateTenant, onBack }: ConfirmLandingChoicesProps) => {
  const [isCreating, setIsCreating] = useState(false);
  const { closeModal } = useModal();
  const router = useRouter();
  const landingSlug = getLandingName(name);
  const isTemplateMode = creationMode === CREATION_MODE.TEMPLATE;
  const templateSourceLocale = templateTenant ? resolveTemplateSourceLocale(templateTenant.supportedLocales) : null;
  const templateSourceLocaleLabel =
    templateSourceLocale && templateSourceLocale in LABELS_BY_LOCALES
      ? LABELS_BY_LOCALES[templateSourceLocale as keyof typeof LABELS_BY_LOCALES]
      : templateSourceLocale;

  const createNewLanding = async () => {
    if (isTemplateMode && !templateTenant) {
      toast.error('No template landing selected. Go back and choose a template.');
      return;
    }

    let createdTenantId: string | null = null;
    let duplicationQueued = false;

    try {
      setIsCreating(true);

      const isAvailable = await isTenantNameAvailable(name);
      if (!isAvailable) {
        toast.error('Landing name is already taken. Go back and choose another.');
        return;
      }

      createdTenantId = await createTenant({ displayName: name, supportedLocales });

      // Always create settings first so the default "do-not-delete" media (favicons, etc.) are
      // seeded for every new landing. In template mode the duplication job updates this same
      // settings doc afterwards with the template content (it detects the existing doc).
      await createSettings(createdTenantId);

      if (isTemplateMode) {
        const duplicateResult = await queueDuplicateTenantContent(templateTenant!.id, createdTenantId);
        if (duplicateResult.ok) {
          duplicationQueued = true;
        } else {
          // Template mode normally gets deploys from the duplication job. If queuing fails,
          // fall back to empty-landing defaults so the tenant is not left without a deploy.
          reportError(
            new ErrorWithContext('Failed to queue content duplication; applying empty landing defaults', {
              failed_step: 'queue_duplicate',
              tenant_id: createdTenantId,
              source_tenant_id: templateTenant!.id,
              error: duplicateResult.error,
            }),
            {
              flow: 'create-new-landing',
              landing_name: name,
              landing_slug: landingSlug,
              creation_mode: creationMode,
            },
          );
          await bootstrapNewLandingDefaults(createdTenantId, landingSlug);
        }
      } else {
        await bootstrapNewLandingDefaults(createdTenantId, landingSlug);
      }

      const { ok: createLandingOk } = await triggerCreateNewLanding(landingSlug, createdTenantId);
      if (!createLandingOk) {
        throw new ErrorWithContext('Proxy failed to create landing', {
          failed_step: 'trigger_proxy',
          tenant_id: createdTenantId,
          duplication_queued: duplicationQueued,
        });
      }

      closeModal(CREATE_NEW_LANDING_MODAL_SLUG);

      if (isTemplateMode && duplicationQueued) {
        toast.success(
          `Landing ${name} created. Content is being copied from ${templateTenant!.displayName} in the background — check Jobs in the admin for progress.`,
        );
      } else if (isTemplateMode) {
        toast.error(
          `Landing "${name}" was created, but template content copy was not queued. Empty landing defaults were applied instead.`,
        );
      } else {
        toast.success(`Landing ${name} created successfully.`);
      }
    } catch (error) {
      reportError(error, {
        flow: 'create-new-landing',
        landing_name: name,
        landing_slug: landingSlug,
        supported_locales: supportedLocales,
        creation_mode: creationMode,
        template_tenant_id: templateTenant?.id,
        tenant_id: createdTenantId,
        duplication_queued: duplicationQueued,
      });

      closeModal(CREATE_NEW_LANDING_MODAL_SLUG);

      const detail = error instanceof Error && error.message ? error.message : 'unknown error';
      if (createdTenantId) {
        toast.error(
          duplicationQueued
            ? `Landing "${name}" was created but setup did not finish (${detail}). Content copy may still be running — check Jobs in the admin.`
            : `Landing "${name}" was created but setup did not finish (${detail}). It appears in the landings list.`,
        );
      } else {
        toast.error(`Failed to create landing: ${detail}`);
      }
    } finally {
      router.refresh();
      setIsCreating(false);
    }
  };

  return (
    <div data-testid="create-new-landing-step-confirm">
      {isCreating && <LoadingOverlay />}
      <WizardStepHeader title="Confirm new landing creation" onBack={onBack} backDisabled={isCreating} />
      <p>You are about to create a new landing with the following details:</p>
      <ul>
        <li>
          <b>Name:</b> <span data-testid="create-new-landing-confirm-name">{name}</span>
        </li>
        <li>
          <b>Internal name:</b> <span data-testid="create-new-landing-confirm-internal-name">{landingSlug}</span>
        </li>
        <li>
          <b>Supported locales:</b> <span data-testid="create-new-landing-confirm-locales">{supportedLocales.join(', ')}</span>.
        </li>
        <li>
          <b>Creation mode:</b>{' '}
          <span data-testid="create-new-landing-confirm-creation-mode">{isTemplateMode ? 'From template' : 'Empty landing'}</span>
        </li>
        {isTemplateMode && templateTenant && (
          <li>
            <b>Template:</b> <span data-testid="create-new-landing-confirm-template">{templateTenant.displayName}</span>
          </li>
        )}
      </ul>
      {isTemplateMode && templateTenant && templateSourceLocaleLabel && (
        <p data-testid="create-new-landing-confirm-template-locale-copy">
          Template content from <b>{templateSourceLocaleLabel}</b> will be copied into every selected locale ({supportedLocales.join(', ')}
          ). Translate content in the CMS after creation.
        </p>
      )}
      {isTemplateMode && !templateSourceLocaleLabel && (
        <p>Content from the template will be copied in the background after the landing is created.</p>
      )}
      <div className="fv-create-new-landing-modal__step-actions">
        <Button
          type="button"
          buttonStyle="secondary"
          onClick={() => closeModal(CREATE_NEW_LANDING_MODAL_SLUG)}
          disabled={isCreating}
          extraButtonProps={{ 'data-testid': 'create-new-landing-confirm-cancel' }}
        >
          Cancel
        </Button>
        <Button
          type="button"
          buttonStyle="primary"
          onClick={() => void createNewLanding()}
          disabled={isCreating}
          extraButtonProps={{ 'data-testid': 'create-new-landing-confirm-create' }}
        >
          Create landing
        </Button>
      </div>
    </div>
  );
};
